import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, PhoneCall, Trash2, Users, Clock, CheckCircle, XCircle,
  RefreshCw, Plus, X,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ToastMsg from '../../../components/common/ToastMsg';
import {
  ENGAGE_AI_CALLER, PRIORITY_BADGE, prettyStatus, prettyOutcome,
} from './aiCallerConstants';
import { getEngageStatusBadge, ENGAGE_STATUS_LABEL } from '../../../utils/engageStageBadge';


const BUCKET_STATUS_META = {
  pending:   { label: 'Pending',   icon: Clock,       tone: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  calling:   { label: 'Calling',   icon: PhoneCall,   tone: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400' },
  completed: { label: 'Completed', icon: CheckCircle, tone: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' },
  exhausted: { label: 'Exhausted', icon: XCircle,     tone: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
};


export default function EngageAICallerBucket() {
  const navigate = useNavigate();
  const { fetch: apiFetch, create: apiCreate, remove: apiDelete } = useApiMethods({ throwOnFail: true });

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, calling: 0, completed: 0, exhausted: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [priority, setPriority] = useState('medium');
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [notes, setNotes] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (statusFilter !== 'all')   q.set('status', statusFilter);
      if (priorityFilter !== 'all') q.set('priority', priorityFilter);
      const resp = await apiFetch(`${ENGAGE_AI_CALLER.BUCKET}?${q.toString()}`);
      setItems(resp?.data || resp?.data?.data || []);
      setSummary(resp?.summary || resp?.data?.summary || { pending: 0, calling: 0, completed: 0, exhausted: 0 });
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load bucket');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, reloadTick]);

  useEffect(() => { load(); }, [load]);

  const openAdd = async () => {
    setAddOpen(true);
    try {
      const resp = await apiFetch('engage/enquiries/?limit=200');
      const list = resp?.results || resp?.data?.results || [];
      // Filter out enquiries without mobile
      setEnquiries(list.filter(e => e.mobile));
    } catch (e) {
      ToastMsg('error', 'Failed to load enquiries');
    }
  };

  const handleAdd = async () => {
    if (selectedIds.length === 0) {
      ToastMsg('error', 'Please select at least one enquiry');
      return;
    }
    try {
      const resp = await apiCreate(ENGAGE_AI_CALLER.BUCKET, {
        enquiry_ids: selectedIds, priority, max_attempts: maxAttempts, notes,
      });
      const d = resp?.data || {};
      ToastMsg('success', `Added ${d.added} enquiries (skipped ${d.skipped_existing + d.skipped_no_phone})`);
      setAddOpen(false);
      setSelectedIds([]);
      setNotes('');
      setReloadTick(t => t + 1);
    } catch (e) {
      ToastMsg('error', e?.response?.data?.message || 'Failed to add');
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove from bucket?')) return;
    try {
      await apiDelete(`${ENGAGE_AI_CALLER.BUCKET}${id}/`);
      ToastMsg('success', 'Removed');
      setReloadTick(t => t + 1);
    } catch {
      ToastMsg('error', 'Failed to remove');
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear all completed / exhausted items?')) return;
    try {
      const resp = await apiCreate(ENGAGE_AI_CALLER.BUCKET_CLEAR, {});
      ToastMsg('success', resp?.message || 'Cleared');
      setReloadTick(t => t + 1);
    } catch {
      ToastMsg('error', 'Failed to clear');
    }
  };

  const toggle = (id) => setSelectedIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/ai-caller/dashboard')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to AI Voice Agent
      </button>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white shadow-sm">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Call Bucket</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Enquiries queued for AI auto-calling</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleClear} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50">
            <Trash2 className="w-3.5 h-3.5" /> Clear done
          </button>
          <button onClick={openAdd} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-purple-500 to-indigo-600">
            <Plus className="w-3.5 h-3.5" /> Add to bucket
          </button>
        </div>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {['pending', 'calling', 'completed', 'exhausted'].map(k => {
          const M = BUCKET_STATUS_META[k];
          return (
            <button key={k} onClick={() => setStatusFilter(statusFilter === k ? 'all' : k)}
              className={cn('bg-white dark:bg-gray-800 rounded-xl p-3 border transition-all text-left',
                statusFilter === k
                  ? 'border-purple-400 dark:border-purple-500 shadow-md'
                  : 'border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600')}>
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center mb-1.5', M.tone)}>
                <M.icon className="w-4 h-4" />
              </div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">{M.label}</div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">{summary[k] || 0}</div>
            </button>
          );
        })}
      </div>

      {/* Priority filter chips */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500">Priority:</span>
        {['all', 'high', 'medium', 'low'].map(p => (
          <button key={p} onClick={() => setPriorityFilter(p)}
            className={cn('px-3 py-1 rounded-full text-xs font-medium capitalize',
              priorityFilter === p
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50')}>
            {p === 'all' ? 'All' : p}
          </button>
        ))}
        <button onClick={() => setReloadTick(t => t + 1)} className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Enquiry</th>
              <th className="px-4 py-2 text-left font-medium">Mobile</th>
              <th className="px-4 py-2 text-left font-medium">Enquiry stage</th>
              <th className="px-4 py-2 text-center font-medium">Priority</th>
              <th className="px-4 py-2 text-center font-medium">Attempts</th>
              <th className="px-4 py-2 text-left font-medium">Bucket status</th>
              <th className="px-4 py-2 text-left font-medium">Last outcome</th>
              <th className="px-4 py-2 text-center font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">Loading…</td></tr>
            : items.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Bucket is empty for this filter.</td></tr>
            : items.map(it => (
              <tr key={it.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="px-4 py-2.5">
                  <Link to={`/engage/enquiries/${it.enquiry}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                    {it.enquiry_name || `Enquiry #${it.enquiry}`}
                  </Link>
                </td>
                <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{it.enquiry_mobile || '—'}</td>
                <td className="px-4 py-2.5">
                  {it.enquiry_status && <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageStatusBadge(it.enquiry_status))}>
                    {ENGAGE_STATUS_LABEL[it.enquiry_status] || it.enquiry_status}
                  </span>}
                </td>
                <td className="px-4 py-2.5 text-center">
                  <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium capitalize', PRIORITY_BADGE[it.priority] || 'bg-gray-100 text-gray-700')}>
                    {it.priority}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-center text-gray-700 dark:text-gray-300">{it.attempt_count} / {it.max_attempts}</td>
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300">
                    {(() => { const I = BUCKET_STATUS_META[it.status]?.icon; return I ? <I className="w-3.5 h-3.5" /> : null; })()}
                    {prettyStatus(it.status)}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">{it.last_outcome ? prettyOutcome(it.last_outcome) : '—'}</td>
                <td className="px-4 py-2.5 text-center">
                  <button onClick={() => handleRemove(it.id)} className="text-gray-400 hover:text-red-500" title="Remove">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add-to-bucket modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAddOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-white">Add enquiries to bucket</h2>
              <button onClick={() => setAddOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase text-gray-500 font-medium">Priority</label>
                  <select value={priority} onChange={e => setPriority(e.target.value)}
                    className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white">
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase text-gray-500 font-medium">Max attempts</label>
                  <input type="number" value={maxAttempts} min={1} max={10} onChange={e => setMaxAttempts(Number(e.target.value))}
                    className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white" />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase text-gray-500 font-medium">Notes (optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  placeholder="e.g. Post-Diwali no-show recovery"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] uppercase text-gray-500 font-medium">Select enquiries ({selectedIds.length} selected)</label>
                  <button onClick={() => setSelectedIds(enquiries.map(e => e.id))} className="text-xs text-blue-600 hover:text-blue-700">Select all</button>
                </div>
                <div className="max-h-[300px] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  {enquiries.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 text-center dark:text-gray-400">No enquiries with mobile numbers.</p>
                  ) : enquiries.map(e => (
                    <label key={e.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <input type="checkbox" checked={selectedIds.includes(e.id)} onChange={() => toggle(e.id)} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{e.name}</div>
                        <div className="text-[11px] text-gray-500">{e.mobile} • {e.status}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
              <button onClick={() => setAddOpen(false)} className="px-3 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleAdd} disabled={selectedIds.length === 0} className="px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-purple-500 to-indigo-600 disabled:opacity-50">
                Add {selectedIds.length} to bucket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
